import { type FC, memo, useCallback, useMemo, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import styled from 'styled-components'
import { AnimatePresence, motion, Variants } from 'motion/react'
import { DY_GUEST_CART_ALLOWED, DY_LOGIN_ALLOWED, IS_HM_STORE, IS_MMF_STORE, LANG, STORE_ABBR } from '@/utils'
import { createUnderline, designGrid, getTransition, mq, presets, vw } from '@/styles'
import { Img, Link } from '../../base'
import { useDeviceType, useModal, useScrollTracker, useSession, useTranslator } from '@/hooks'
import { useCart } from '@/context/project/CartContext'
import { MODALS } from '@/context'
import { CartSummaryDropdown } from '../cart/CartSummaryDropdown'
import type { TypeCmsLayout } from '@/schema'
import { ButtonTriggerSearch } from '../search'
import { useLocation } from '@/hooks'

const logo = (white: boolean) => ({
  mmf: { type: 'svg' as const, src: `/images/svg/logo--mmf${white ? '--white' : ''}.svg`, alt: 'Logo', size: { width: 261, height: 66 } },
  hm: { type: 'svg' as const, src: `/images/svg/logo--hm${white ? '--white' : ''}.svg`, alt: 'Logo', size: { width: 240, height: 31 } },
})

type Props = { data: TypeCmsLayout['categories'] }

type TypeNavItem = { name: string; href: string; order: number; stores: string[]; visibility: 'private' | 'public' | 'both' }

const getNavItems = ({ storeName, isPrivate, navItems }: { storeName: string; isPrivate: boolean; navItems: TypeNavItem }) => {
  if (!navItems || !Array.isArray(navItems)) return
  return navItems
    .filter(item => {
      const matchVisibility = item.visibility === 'both' || item.visibility === (isPrivate ? 'private' : 'public')

      const matchStore = !item.stores || item.stores.includes(storeName)
      return matchVisibility && matchStore
    })
    .sort((a, b) => a.order - b.order)
}

const getNav = (navItems: TypeNavItem[], firstCategory: string) =>
  navItems.map((item, idx) => {
    const { name, href } = item

    return (
      <motion.li
        key={idx}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 + idx * 0.2, duration: 1, ease: 'easeInOut' }}
      >
        <Link href={idx === 0 ? `${href}/${firstCategory}` : href} className='nav--item'>
          {name}
        </Link>
      </motion.li>
    )
  }
)

const LEVEL = IS_MMF_STORE ? 'login' : 'login_hm'

export const Nav: FC<Props> = memo(({ data }) => {
  const { t } = useTranslator()
  const { toggle: toggleRegionStore } = useModal(MODALS.REGION_STORE)
  const { updatePayload: updateAuthPayload } = useModal(MODALS.AUTH)
  const { isActive:isMenuActive, toggle: toggleMenu } = useModal(MODALS.MENU)
  const { toggle: toggleMenuAccount } = useModal(MODALS.MENU_ACCOUNT)
  const { push } = useRouter()
  const pathname = usePathname()

  const { scrollOnTop } = useScrollTracker(2)
  const { user, sessionChecked } = useSession()
  const { cartLines, isLoading:isLoadingCart } = useCart()
  const { isMobile } = useDeviceType()
  const { location } = useLocation()

  const firstCategory = data?.[0]?.slug?.[LANG]
  const navItems = useMemo(
    () =>
      !sessionChecked
        ? []
        : getNavItems({
            storeName: STORE_ABBR,
            isPrivate: !!user?.bigcommerceId,
            navItems: t('project.nav') as unknown as TypeNavItem,
          }),
    [user?.bigcommerceId, t, sessionChecked]
  )
  const Items = useMemo(() => getNav(navItems, firstCategory), [navItems, firstCategory])

  const [cartHover, setCartHover] = useState(false)

  const onClickAccount = useCallback(() => {
    if (!user?.firstName) updateAuthPayload({ payload: { level: LEVEL }, open: true })
    else if (isMobile) toggleMenuAccount()
    else push('/account/orders')
  }, [push, updateAuthPayload, user, isMobile, toggleMenuAccount])

  const onClickMenu = useCallback(() => {
    toggleMenu()
  }, [toggleMenu])

  return (
    <Navbar aria-label={MAIN_NAVIGATION_TRANSLATION[LANG]} $isMobileNavExpanded={isMenuActive} $scrollOnTop={scrollOnTop} $isLoadingCart={isLoadingCart}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.5, ease: 'easeInOut' }}>
        <button
          type='button'
          aria-haspopup='dialog'
          className='btn btn--menu'
          aria-controls={MODALS.MENU}
          aria-label='Menu'
          onClick={onClickMenu}
        >
          {isMenuActive ?
						<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'><g transform='matrix(-.707107 -.707107 .707107 -.707107 8 22.142136)' stroke='white' fill='none' stroke-linecap='round' stroke-width='2.036'><path d='M.909 10h18.183'/><path d='M10 19.091V.909'/></g></svg>
					:
						<svg aria-hidden='true' xmlns='http://www.w3.org/2000/svg' width='22' height='17' viewBox='0 0 22 17'>
							<path
								d='M.68 1.58h21M.68 8.5h21m-21 6.92h21'
								stroke={isMenuActive ? 'white' : 'var(--color-text)'}
								strokeWidth='2.33'
								fill='none'
								strokeLinejoin='round'
							/>
						</svg>
					}
        </button>
        {!pathname.includes('/maintenance') && <ButtonTriggerSearch fillColor={isMenuActive ? '--color-white' : '--color-text'} />}
        <Link href='/' className='l--logo' aria-label='Go to homepage'>
          <Img media={logo(isMenuActive)[STORE_ABBR]} />
        </Link>
        {IS_MMF_STORE && (
          <>
            <ul>{Items}</ul>
            <button
              type='button'
              aria-haspopup='dialog'
              className='btn btn--location'
              aria-controls={MODALS.REGION_STORE}
              onClick={toggleRegionStore}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                viewBox='0 0 14 18'
                width='14'
                height=' 8'
                fill={isMenuActive ? 'white' : 'var(--color-text)'}
              >
                <path d='M7 .11a6.8 6.8 0 0 0-5.48 2.75 7.1 7.1 0 0 0 0 8.47l3.55 4.66.74.95.37.47.14.16v.02l.04.04.06.03.46.18.13.05.55-.21.1-.05.03-.02v-.02-.02l.2-.24.42-.53 1.09-1.42 3.09-4.06a7.1 7.1 0 0 0 0-8.48A6.8 6.8 0 0 0 7 .11zm0 1.78a5 5 0 0 1 4.06 2.04 5.3 5.3 0 0 1 0 6.34l-3.39 4.45-.67.86-1.15-1.49-2.92-3.83a5.3 5.3 0 0 1 0-6.34C3.91 2.63 5.39 1.89 7 1.89zm-.02 7.08c-1.01 0-1.84-.84-1.84-1.88s.83-1.88 1.84-1.88 1.84.84 1.84 1.88S8 8.97 6.98 8.97z' />
              </svg>
              <span>{location?.name}</span>
            </button>
          </>
        )}
        {(!pathname.includes('/maintenance') && DY_LOGIN_ALLOWED) &&
          <button
            type='button'
            aria-haspopup='dialog'
            className='btn btn--account'
            aria-controls={MODALS.AUTH}
            onClick={onClickAccount}
          >
            <svg aria-hidden='true' xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18'>
              <path
                d='M9.026 0c2.994 0 5.436 2.385 5.436 5.31 0 1.802-.928 3.4-2.34 4.361C15.574 10.911 18.04 14.166 18 18l-1.847-.051c0-3.854-3.181-6.961-7.127-6.961S1.899 14.095 1.847 18L0 17.949c0-3.794 2.475-7.041 5.929-8.28-1.412-.961-2.34-2.558-2.34-4.36C3.59 2.385 6.031 0 9.026 0zm0 1.854c-1.962 0-3.538 1.539-3.538 3.455s1.575 3.455 3.538 3.455 3.538-1.539 3.538-3.455-1.575-3.455-3.538-3.455z'
                fill={isMenuActive ? 'white' : 'var(--color-text)'}
                fillRule='evenodd'
              />
            </svg>
            <AnimatePresence mode='wait'>
              <motion.span
                key={user?.firstName ?? 'default'}
                layout
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{
                  opacity: { duration: 0.3, ease: 'easeInOut' },
                  y: { duration: 0.3, ease: 'easeInOut' },
                  layout: { duration: 0.3, ease: 'easeInOut' },
                }}
                style={{ display: 'inline-block' }}
              >
                {user?.firstName ?? t('project.account_label')}
              </motion.span>
            </AnimatePresence>
          </button>
        }
        {(!pathname.includes('/maintenance') && (IS_HM_STORE || user?.firstName)) && (
          <AnimatePresence mode='wait'>
            <motion.div
              className='cart-wrapper'
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 'auto', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{
                width: { duration: 0.4, ease: 'easeInOut' },
                opacity: { duration: 0.3, ease: 'easeInOut', delay: 0.4 },
              }}
              onMouseOver={() => {
                if(cartLines?.length > 0) setCartHover(true)
              }}
              onMouseLeave={() => {
                setCartHover(false)
              }}
            >
              <div style={{ overflow: 'hidden' }}>
                <Link href='/cart' {...((DY_GUEST_CART_ALLOWED && !user) ? { onClick: (e) => {
									e.preventDefault()
									e.stopPropagation()
									updateAuthPayload({ payload: { level: IS_MMF_STORE ? 'login' : 'login_hm' }, open: true })
								}} : {})} aria-controls='dropdown--cart-preview' aria-label='Shopping Cart'>
                  <svg
                    aria-hidden='true'
                    xmlns='http://www.w3.org/2000/svg'
                    width='21'
                    height='18'
                    viewBox='0 0 21 18'
                    fill={isMenuActive ? 'white' : 'var(--color-text)'}
                  >
                    <path d='M7.499 18C8.36 18 9 17.37 9 16.487 9 15.579 8.337 15 7.475 15S6 15.63 6 16.487C6 17.37 6.639 18 7.501 18m7.998 0c.859 0 1.501-.63 1.501-1.513 0-.908-.664-1.487-1.524-1.487A1.43 1.43 0 0 0 14 16.487c0 .883.64 1.513 1.501 1.513m5.326-13.98c-.168-.221-.437-.352-.723-.352H4.77L4.034.638A.89.89 0 0 0 3.169 0H.894C.4 0 0 .382 0 .854s.4.854.894.854H2.48l3.156 11.659c.105.373.459.632.864.632h10.698c.388-.001.731-.24.851-.592l2.911-8.629a.82.82 0 0 0-.131-.759zm-4.285 8.277H7.18l-1.683-5.99-.257-.93h13.638l-2.336 6.92z' />
                  </svg>
                  <AnimatePresence>
                    {cartLines?.length > 0 &&
                      <motion.span className='counter' aria-live='polite' initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} transition={{ type: 'spring', stiffness: 500, damping: 30 }}>
                        {cartLines?.length > 0 ? cartLines.length : ''}
                      </motion.span>
                    }
                  </AnimatePresence>
                </Link>
              </div>
              <AnimatePresence mode='wait'>
                {(!pathname.includes('/cart') && cartHover) && (
                  <motion.div
                    id='dropdown--cart-preview'
                    aria-label={SHOPPING_CART_PREVIEW_TRANSLATION[LANG]}
                    variants={listVariants}
                    initial='inactive'
                    animate={'enter'}
                    exit={'exit'}
                  >
                    <ul>
                      <CartSummaryDropdown />
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </AnimatePresence>
        )}
      </motion.div>
    </Navbar>
  )
})

const Navbar = styled.nav<{ $isMobileNavExpanded: boolean, $scrollOnTop?: boolean, $isLoadingCart?: boolean }>`
  ${designGrid({})}
  align-items: center;
  background-color: ${({ $isMobileNavExpanded }) =>
    $isMobileNavExpanded ? 'var(--color-text)' : 'var(--color-navbar-background)'};
  box-shadow: ${({ $scrollOnTop, $isMobileNavExpanded }) => ($scrollOnTop || $isMobileNavExpanded) ? 'none' : 'rgba(0, 0, 0, 0.1) 0px 0px 30px 0px'};
  height: 60px;
  flex-direction: row;
  padding: 6px 0;
  position: sticky;
  top: 0;
  width: 100dvw;
  width: 100vw;
  z-index: 200;
  transition: background-color 550ms ease, box-shadow 550ms ease;

  > div {
    align-items: center;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    height: 100%;
    justify-content: flex-start;
    grid-column: 1 / span 6;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
    }

    ${mq.greaterThan('container')} {
      margin: 0 auto;
      max-width: var(--page-container-max-width);
    }

    > ul {
      align-items: center;
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: flex-start;
    }

    > ul {
      ${mq.lessThan('tablet')} {
        display: none;
      }

      a {
        font-weight: bold;
      }
    }
  }

  li {
    align-items: center;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;

    a {
      ${presets.font17NavLinks}
      ${createUnderline({ height: 2 })}

      ${mq.greaterThan('tablet')} {
        margin: 0 ${vw(15, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        margin: 0 15px;
      }
    }
  }

  a,
  button {
    height: 100%;

    span {
      font-weight: 500;
      ${presets.font17NavLinks}
    }
  }

  .btn--account,
  .btn--location {
    span {
      font-family: var(--font-basier-circle);
      ${presets.font14Search}
    }
  }

  .btn--account {
    span {
      ${mq.lessThan('tablet')} {
        display: none !important;
      }
    }
  }

  .cart-wrapper {
    > a {
      span {
        font-family: var(--font-basier-circle);
        ${presets.font14Search}
      }
    }
  }

  .l--logo {
    align-items: center;
    display: flex;
    margin: 0 auto;
    max-width: 240px;
    width: ${vw(123, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      width: ${vw(123, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin: 0;
      margin-right: ${vw(30, 'desktop')};
      width: ${vw(184, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-right: ${vw(30, 'desktop')};
      width: 184px;
    }
  }

  .btn {
    align-items: center;
    display: flex;
    flex-wrap: nowrap;
    gap: ${vw(10, 'mobile')};
    height: 100%;
    justify-content: center;
    min-width: 48px;
    padding: 0 ${vw(4, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      gap: ${vw(10, 'tablet')};
      padding: 0 ${vw(4, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      gap: ${vw(10, 'desktop')};
      margin: 0 ${vw(4, 'desktop')};
      order: 2;
      padding: 0 ${vw(4, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      gap: 10px;
      margin: 0 4px;
      padding: 0 4px;
    }

    span {
      ${mq.lessThan('tablet')} {
        display: none;
      }
    }

    svg,
    img {
			min-height: 18px;
      width: 16px;

			${mq.greaterThan('tablet')} {
				width: 14px;
			}
    }

    &.btn--menu {
      margin-left: 0;
      padding-left: 0;

      ${mq.greaterThan('tablet')} {
        display: none;
      }
    }

    &.btn--search {
      font-family: var(--font-basier-circle);

      ${mq.greaterThan('tablet')} {
        background-color: var(--color-white);
        border: 1px solid var(--color-white);
        border-radius: 50px;
        margin-left: auto;
        margin-right: ${vw(30, 'desktop')};
        max-height: 38px;
        height: 40px;
        padding: 0 ${vw(20, 'desktop')};
        transition: 250ms border-color linear;
      }

      ${mq.greaterThan('desktop')} {
        margin-right: 30px;
        padding: 0 20px;
      }

      &:hover {
        border-color: var(--color-text--darker);
      }

      span {
        font-weight: 400;
      }
    }
  }

  .cart-wrapper {
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-end;
    position: relative;
    padding-left: ${vw(4, 'mobile')};
    transform-origin: right;
    transition:
      margin-left 300ms ease-in-out,
      padding-left 300ms ease-in-out;
    overflow: visible;

    ${mq.greaterThan('nexus7')} {
      padding-left: ${vw(4, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-left: ${vw(4, 'desktop')};
      order: 3;
      padding-left: ${vw(4, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-left: 4px;
      padding-left: 4px;
    }

    > div {
      > a {
        display: flex;
        flex-wrap: nowrap;
        white-space: nowrap;
      }
    }


    svg {
      width: ${vw(16, 'mobile')};

      ${mq.greaterThan('tablet')} {
        width: ${vw(16, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        width: 16px;
      }
    }

    .counter {
      align-items: center;
      align-self: flex-end;
      background-color: var(--color-gold);
      border-radius: 10px;
      color: var(--color-gold);
      display: flex;
      font-family: var(--font-graphik);
      font-size: 7px;
      height: 8px;
      justify-content: center;
      overflow: hidden;
      position: relative;
      width: 8px;

      &:after {
        animation: snakeTurn 1s infinite linear;
        aspect-ratio: 1;
        background:
          radial-gradient(farthest-side,var(--color-text) 94%, #0000) top/2px 2px no-repeat,
          conic-gradient(#0000 30%,var(--color-text));
        mask: radial-gradient(farthest-side,#0000 calc(100% - 2px),#000 0);
        -webkit-mask: radial-gradient(farthest-side,#0000 calc(100% - 2px),#000 0);
        border-radius: 10px;
        content: '';
        height: 100%;
        left: 0;
        opacity: ${({ $isLoadingCart }) => ($isLoadingCart ? 1 : 0)};
        position: absolute;
        right: 0;
        top: 0;
        transition: 200ms opacity ease-in-out;
        z-index: 3;
        width: 100%;
      }

      @keyframes snakeTurn{
        100% { transform: rotate(1turn) }
      }
    }
  }

  #dropdown--cart-preview {
    min-width: ${vw(308, 'mobile')};
    position: absolute;
    right: 0;
    top: 100%;
    width: max-content;
    z-index: 5000;

    ${mq.greaterThan('nexus7')} {
      min-width: ${vw(308, 'tablet')};
      /* top: calc(100% + ${vw(10, 'tablet')}); */
    }

    ${mq.greaterThan('tablet')} {
      min-width: ${vw(308, 'desktop')};
      /* top: calc(100% + ${vw(10, 'desktop')}); */
    }

    ${mq.greaterThan('desktop')} {
      min-width: 308px;
      /* top: calc(100% + 10px); */
    }

    > ul {
      list-style: none;
      margin: 0;
      padding: 0;
      position: relative;
    }
  }
`

const listVariants: Variants = {
  inactive: { opacity: 0, ...getTransition(0.5) },
  enter: { opacity: 1, transition: { ...getTransition(0.5), staggerChildren: 0.01 } },
  exit: { opacity: 0, ...getTransition(0.5) },
}

const MAIN_NAVIGATION_TRANSLATION = {
  es: 'Navegación principal del sitio',
  en: 'Main site navigation',
  fr: 'Navigation principale du site',
  de: 'Hauptnavigation der Website',
  pt: 'Navegação principal do site',
  it: 'Navigazione principale del sito',
}

const SHOPPING_CART_PREVIEW_TRANSLATION = {
  es: 'Vista previa del carrito de compras',
  en: 'Shopping Cart Preview',
  fr: 'Aperçu du panier',
  de: 'Vorschau des Warenkorbs',
  pt: 'Pré-visualização do carrinho de compras',
  it: 'Anteprima del carrello',
}
